import React, { useState, useRef, useEffect } from 'react';
import OtpStageUI from './OtpStage.ui';
import { validationRules } from 'app/utilities/validation/validationVariables';

const OtpStage = ({ onSubmit, onResend, data = {}, error: externalError }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [restrictedCode, setRestrictedCode] = useState('');
  const [restrictedCodeError, setRestrictedCodeError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const isMountedRef = useRef(true);
  const isSubmittingRef = useRef(false);
  const autoSubmitTimeoutRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Clear any pending auto-submit timeout on unmount
      if (autoSubmitTimeoutRef.current) {
        clearTimeout(autoSubmitTimeoutRef.current);
      }
    };
  }, []);

  // Update local error when external error changes
  useEffect(() => {
    if (externalError) {
      setError(externalError);
    }
  }, [externalError]);

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState('');

  const submit = async (e, codeOverride) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!onSubmit) return;

    // Prevent double submission
    if (isSubmittingRef.current) {
      return;
    }

    // Use override code if provided (for auto-submit), otherwise use state
    const currentCode = codeOverride !== undefined ? codeOverride : code;

    // Validate OTP code is entered
    if (!currentCode || currentCode.toString().trim() === '') {
      setError('לא הוזן קוד. שננסה שוב?');
      return;
    }

    if (showRestricted && (!restrictedCode || restrictedCode.toString().trim() === '')) {
      setRestrictedCodeError('לא הוזן קוד זיהוי. שננסה שוב?');
      return;
    }

    if (data && data.showTerms && !termsAccepted) {
      setTermsError('כדי להמשיך יש לאשר את תקנון האתר');
      return;
    }

    isSubmittingRef.current = true;
    if (isMountedRef.current) setLoading(true);
    if (isMountedRef.current) setError('');
    if (isMountedRef.current) setTermsError('');
    if (isMountedRef.current) setRestrictedCodeError('');
    
    try {
      const result = onSubmit({ code: currentCode, restrictedCode, termsAccepted });
      if (result && typeof result.then === 'function') {
        await result;
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        isSubmittingRef.current = false;
      }
    }
  };

  const handleResend = async () => {
    if (!onResend) return;
    try {
      if (isMountedRef.current) setResendLoading(true);
      const result = onResend();
      if (result && typeof result.then === 'function') {
        await result;
      }
    } finally {
      if (isMountedRef.current) setResendLoading(false);
    }
  };

  const showRestricted = data && data.showRestricted === true;

  const onTermsChange = (val) => {
    setTermsAccepted(!!val);
    if (termsError && val) setTermsError('');
  };

  const clearRestrictedError = () => {
    if (restrictedCodeError) setRestrictedCodeError('');
  };

  const handleCodeChange = (value) => {
    // Limit to 6 digits
    const digits = (value || '').toString().replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    // Clear error when user starts typing
    if (error) setError('');
    
    // Clear any pending auto-submit timeout
    if (autoSubmitTimeoutRef.current) {
      clearTimeout(autoSubmitTimeoutRef.current);
      autoSubmitTimeoutRef.current = null;
    }
    
    // Auto-submit when 6 digits are entered (typically from paste)
    // But only if no restricted code or terms acceptance is required
    if (digits.length === 6 && !showRestricted && !(data && data.showTerms)) {
      // Pass the digits directly to submit to avoid state timing issues
      autoSubmitTimeoutRef.current = setTimeout(() => {
        submit(null, digits);
      }, 100);
    }
  };

  const onOpenTerms = () => {
    if (data && data.termsUrl) {
      window.open(data.termsUrl, '_blank');
    }
  };

  return (
      <OtpStageUI
        phone={data.phone}
        onSubmit={submit}
        codeValue={code}
        onCodeChange={handleCodeChange}
        restrictedCodeValue={restrictedCode}
        onRestrictedCodeChange={Object.assign((v) => setRestrictedCode(v), { clearError: clearRestrictedError })}
        showRestricted={showRestricted}
        restrictedCodeError={restrictedCodeError}
        loading={loading}
        resendLoading={resendLoading}
        onResend={handleResend}
        termsInd={data && data.showTerms}
        termsAccepted={termsAccepted}
        onTermsChange={onTermsChange}
        termsError={termsError}
        onOpenTerms={onOpenTerms}
        error={error}
      />
  );
};

export default OtpStage;
